import React from "react";
import styled from "styled-components";
import _ from "lodash";
import Highchart from "./Highchart";

import { toleranceLegendItem } from "../utils/groundTruthPlotLines";

import {
  HeightOffsetHeightBiasConfig,
  HeightOffsetSideBiasConfig,
  OffsetChartConfig,
  SideOffsetHeightBiasConfig,
  SideOffsetSideBiasConfig,
  WsuPitchLocationsConfig
} from "./charts/GroundTruthChartConfigs";

const ChartContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const ChartWrapper = styled.div`
  flex-basis: 50%;
  max-width: 50%;
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const CHARTS_TO_RENDER = [
  OffsetChartConfig,
  WsuPitchLocationsConfig,
  HeightOffsetSideBiasConfig,
  SideOffsetSideBiasConfig,
  HeightOffsetHeightBiasConfig,
  SideOffsetHeightBiasConfig
];

class GroundTruthChart extends React.Component {
  constructor(props) {
    super(props);
    this._createChart = this._createChart.bind(this);
  }

  _createHighchartsConfig(config, data) {
    const series = [];

    config.seriesConfigs.forEach(sm => {
      const seriesData = sm.validator(data).map(record => sm.mapper(record));
      const smSeries = {
        name: sm.name,
        type: "scatter",
        data: seriesData,
        color: sm.color,
        marker: {
          symbol: "diamond"
        },
        tooltip: {
          pointFormatter: function() {
            return `
             ${this.pitcherType}<br/>x: <b>${this.x}</b><br/>y: <b>${
              this.y
            }</b><br/>`;
          }
        }
      };

      if (!_.isEmpty(seriesData)) {
        series.push(smSeries);

        if (sm.regressionLine) {
          series.push({
            name: sm.regressionLine.name,
            type: "line",
            data: sm.regressionLine.mapper(seriesData),
            color: sm.regressionLine.color
          });
        }
      }
    });

    const toleranceSeries = toleranceLegendItem(config.xAxis, config.yAxis);
    if (toleranceSeries) {
      series.push(toleranceSeries);
    }

    const options = {
      title: {
        text: config.title
      },
      xAxis: config.xAxis,
      yAxis: config.yAxis,
      legend: {
        //this allows for 2 values per row in the legend
        width: 400,
        itemWidth: 200
      },
      plotOptions: {
        line: {
          dashStyle: "ShortDot",
          marker: {
            enabled: false
          }
        }
      },
      series: series
    };

    return options;
  }

  _createChart(id, options, width, aspectRatio) {
    const chart =
      width && aspectRatio ? (
        // If the config has specified dimensions, wrap the chart into a centering container.
        // Discussion here: https://github.mlbam.net/bdata/seti-web/pull/82#issue-244702
        <Center>
          <Highchart
            containerId={`ground-truth-${id}`}
            options={options}
            width={width}
            height={width / aspectRatio}
          />
        </Center>
      ) : (
        <Highchart containerId={`ground-truth-${id}`} options={options} />
      );

    return <ChartWrapper key={id}>{chart}</ChartWrapper>;
  }

  _addChartRenderer(options) {
    return _.merge({}, options, {
      chart: {
        events: {
          // This is called on initial chart render and any subsequent re-renders (resizing causes the chart to rerender)
          render: function(chart) {
            // Remove the old svgs on every re-render.
            this.strikeZone && this.strikeZone.destroy();
            this.center && this.center.destroy();

            // Find the axes to translate values to pixels.
            const [xAxis, yAxis] = chart.target.axes;

            // Draw the strike zone box.
            this.strikeZone = chart.target.renderer
              .rect(
                xAxis.toPixels(-1),
                yAxis.toPixels(3.5),
                xAxis.toPixels(1) - xAxis.toPixels(-1),
                // I don't have a good explanation of why it goes to 5.5 other than that it might count from top to bottom.
                yAxis.toPixels(3.5) - yAxis.toPixels(5.5),
                0
              )
              .attr({
                "stroke-width": 2,
                stroke: "green",
                fill: "#efe",
                zIndex: 0
              })
              .add();

            // Draw the center of the strize zone.
            const CENTER_SIZE = 10;
            this.center = chart.target.renderer
              .symbol(
                "plus",
                xAxis.toPixels(0) - CENTER_SIZE / 2,
                yAxis.toPixels(2.5) - CENTER_SIZE / 2,
                CENTER_SIZE,
                CENTER_SIZE
              )
              .attr({
                "stroke-width": 2,
                stroke: "red",
                fill: "transparent",
                zIndex: 5
              })
              .add();
          }
        }
      }
    });
  }

  render() {
    const { pitches } = this.props.data;

    if (!_.isEmpty(pitches)) {
      const charts = CHARTS_TO_RENDER.map(config =>
        this._createChart(
          config.id,
          config.addRender
            ? this._addChartRenderer(
                this._createHighchartsConfig(config, pitches)
              )
            : this._createHighchartsConfig(config, pitches),
          config.width,
          config.aspectRatio
        )
      );
      return <ChartContainer>{charts}</ChartContainer>;
    } else {
      return <div>No data has been submitted for this venue.</div>;
    }
  }
}

export default GroundTruthChart;



// WEBPACK FOOTER //
// ./src/components/GroundTruthChart.js